/**
 * Unauthenticated telehealth guest join via the `chime-guest-join` Medplum Bot.
 *
 * The bot runs as itself (not the caller) and derives all authority from the link code in the
 * URL, so this module never sends an identity of its own.
 */

import type { LocationConfirmationInput } from '@unify/fhir-model/resources/client-location-confirmation';
import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Identifier } from '@medplum/fhirtypes';
import { GUEST_LINK_CODE_IDENTIFIER_SYSTEM } from '@unify/fhir-model/constants';
import {
  botExecuteUrl,
  type ChimeAttendeePayload,
  type ChimeMeetingPayload,
} from './chimeTelehealthService';

export const CHIME_GUEST_JOIN_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'chime-guest-join',
} as const;

export type ChimeGuestWaitResult = {
  /**
   * `unknown` (status): neither waiting nor in the call (they left or were removed; they may
   * join the waiting room again). `ended` (wait, status): the provider ended the session; they
   * may start it again. `needs-location` (wait): send `wait` again with the joiner's `location` (and the
   * returned `joinKey`); it is held on their waiting row and saved to the Appointment when they are admitted.
   */
  state: 'waiting' | 'admitted' | 'unknown' | 'ended' | 'needs-location';
  /** Server-issued per-browser secret; present it on every later call. Never share or log it. */
  joinKey?: string;
  earlyJoin?: boolean;
  /** `wait` while waiting: `start` is more than 15 minutes past and no provider has joined. */
  providerLate?: boolean;
  recordingActive?: boolean;
  /** `status` in the call: a provider's attendee is in the meeting. */
  providerPresent?: boolean;
  /** Admitted to a recorded session: re-call `wait` with `recordingAcknowledged` to get credentials. */
  needsAcknowledgement?: boolean;
  meeting?: ChimeMeetingPayload;
  attendee?: ChimeAttendeePayload;
};

/**
 * sessionStorage key for a link's join key. Hashes the code and the personal-link code, so the
 * plain and personal links of one appointment keep separate keys and neither code is stored.
 */
export function guestJoinKeyStorageKey(code: string, p?: string): string {
  const input = p ? `${code}?p=${p}` : code;
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return `unify.telehealth.joinKey.${(h2 >>> 0).toString(16)}${(h1 >>> 0).toString(16)}`;
}

function botTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_CHIME_GUEST_JOIN_BOT_ID?.trim();
  if (id) return id;
  return {
    system:
      import.meta.env.VITE_MEDPLUM_CHIME_GUEST_JOIN_BOT_IDENTIFIER_SYSTEM?.trim() ??
      CHIME_GUEST_JOIN_BOT_IDENTIFIER.system,
    value:
      import.meta.env.VITE_MEDPLUM_CHIME_GUEST_JOIN_BOT_IDENTIFIER_VALUE?.trim() ??
      CHIME_GUEST_JOIN_BOT_IDENTIFIER.value,
  };
}

export async function chimeGuestWait(
  medplum: MedplumClient,
  input: {
    token: string;
    joinKey?: string;
    p?: string;
    displayName?: string;
    recordingAcknowledged?: boolean;
    /** The attested session location, answering `needs-location`. */
    location?: LocationConfirmationInput;
  },
): Promise<ChimeGuestWaitResult> {
  return medplum.executeBot(
    botTarget(),
    { action: 'wait', ...input },
    ContentType.JSON,
  ) as Promise<ChimeGuestWaitResult>;
}

/** In-call poll for admission and recording state; never returns meeting credentials. */
export async function chimeGuestStatus(
  medplum: MedplumClient,
  input: { token: string; joinKey: string },
): Promise<Pick<ChimeGuestWaitResult, 'state' | 'recordingActive' | 'providerPresent'>> {
  return medplum.executeBot(
    botTarget(),
    { action: 'status', ...input },
    ContentType.JSON,
  ) as Promise<Pick<ChimeGuestWaitResult, 'state' | 'recordingActive' | 'providerPresent'>>;
}

/**
 * Bot refusals that polling cannot fix (expired/invalid link, finished or cancelled session,
 * guest join disabled). Anything else — "temporarily unavailable", network errors — is retried.
 */
export function isTerminalGuestJoinError(message: string): boolean {
  return /expired|not valid|already finished|no longer scheduled|specific time|not enabled/i.test(message);
}

export type GuestJoinRoute = 'loading' | 'signed-in' | 'separate-client' | 'session';

/**
 * How `/join/:code` runs for whoever is signed in:
 *   - `loading`: Medplum is still restoring a stored login — decide nothing yet, since a
 *     not-yet-loaded profile looks anonymous and `session` would replace that login app-wide
 *   - `signed-in`: a Patient or Practitioner; the page looks the code up under their own identity
 *     (`signedInJoinTarget`) before choosing the portal, the appointment, or a separate client
 *   - `separate-client`: any other signed-in user (RelatedPerson), or a stored login whose profile
 *     failed to load — the page uses its own guest-client session and leaves that sign-in untouched
 *   - `session`: no interactive user; the app-wide client signs in as the guest client (`VITE_MEDPLUM_GUEST_CLIENT_ID`)
 */
export function guestJoinRoute(
  profile: { resourceType?: string; id?: string } | undefined,
  auth?: { loading?: boolean; storedLogin?: boolean },
): GuestJoinRoute {
  if (auth?.loading) return 'loading';
  const type = profile?.resourceType;
  if (type === 'Patient' || type === 'Practitioner') return 'signed-in';
  if (type === 'RelatedPerson') return 'separate-client';
  if (!type && auth?.storedLogin) return 'separate-client';
  return 'session';
}

/**
 * Where a signed-in Patient or Practitioner goes for a link, from the Appointment their own
 * session finds for its code (their access policy only returns appointments they may read):
 *   - a Patient participant joins through the portal, under their own identity
 *   - staff join from the appointment (`appointmentId` only when they can read it)
 *   - any other Patient joins as a guest on a separate guest-client session
 */
export function signedInJoinTarget(
  profile: { resourceType?: string; id?: string },
  appointment: Appointment | undefined,
): { kind: 'portal'; appointmentId: string } | { kind: 'staff'; appointmentId?: string } | { kind: 'guest' } {
  if (profile.resourceType === 'Practitioner') return { kind: 'staff', appointmentId: appointment?.id };
  const ref = `Patient/${profile.id}`;
  if (appointment?.id && appointment.participant?.some((p) => p.actor?.reference === ref)) {
    return { kind: 'portal', appointmentId: appointment.id };
  }
  return { kind: 'guest' };
}

/** The Appointment the signed-in user's own session can read for a link code; undefined when none. */
export async function findAppointmentForLinkCode(
  medplum: MedplumClient,
  code: string,
): Promise<Appointment | undefined> {
  const matches = await medplum.searchResources('Appointment', {
    identifier: `${GUEST_LINK_CODE_IDENTIFIER_SYSTEM}|${code}`,
    _count: '2',
  });
  return matches.length === 1 ? matches[0] : undefined;
}

/**
 * Unload-safe counterpart to `chimeGuestLeave` — see `releaseChimeSessionOnUnload`. Never
 * `final`: an unload may be a refresh, which must keep this browser's admission. The bot still
 * drops a not-yet-admitted personal-link row, so the participant's other device can join at once.
 */
export function releaseChimeGuestSessionOnUnload(
  medplum: MedplumClient,
  input: { token: string; joinKey?: string; attendeeId?: string },
): void {
  const accessToken = medplum.getAccessToken();
  if (!accessToken) return;
  void fetch(botExecuteUrl(medplum, botTarget()), {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': ContentType.JSON,
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ action: 'leave', ...input }),
  }).catch(() => undefined);
}

/** Best effort: release an attendee minted for a start that failed, so the provider does not see a guest who never arrived. */
export async function releaseFailedGuestStart(
  medplum: MedplumClient,
  input: { token: string; joinKey?: string; attendeeId?: string },
): Promise<void> {
  if (!input.joinKey || !input.attendeeId) return;
  await chimeGuestLeave(medplum, { token: input.token, joinKey: input.joinKey, attendeeId: input.attendeeId }).catch(
    () => undefined,
  );
}

/** Explicit Leave/decline: gives up the waiting spot or admission (`final`). */
export async function chimeGuestLeave(
  medplum: MedplumClient,
  input: { token: string; joinKey: string; attendeeId?: string },
): Promise<{ left: true }> {
  return medplum.executeBot(
    botTarget(),
    { action: 'leave', final: true, ...input },
    ContentType.JSON,
  ) as Promise<{ left: true }>;
}
